import React, { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import useShop from '../context/AppContext'
import { Link, NavLink } from 'react-router-dom'
import { Menu } from '../container'
import { motion } from 'framer-motion'
import { IconLogo } from '../assets/icons'
import { useScrollDirection } from '../hooks/'
import { headerLinks } from '../config'

const HeaderWrapper = styled.header`
  position: fixed;
  z-index: 11;
  width: 100%;
  height: 100px;
  padding-top: 20px;
  filter: none;
  user-select: auto;
  pointer-events: auto;
  backdrop-filter: blur(10px);
  background-color: var(--black-trans);
  transition: var(--transition);
  ${props =>
    props.scrollDirection === 'up' &&
    !props.scrolledToTop &&
    css`
      padding-top: 5px;
      height: 70px;
      transform: translateY(0px);
      box-shadow: 0 -5px 10px 5px var(--black);
    `};
  ${props =>
    props.scrollDirection === 'down' &&
    !props.scrolledToTop &&
    css`
      padding-top: 5px;
      height: 70px;
      transform: translateY(calc(70px * -1));
      box-shadow: 0 -5px 10px -5px var(--black);
    `};
`
const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 86%;
  height: 100px;
  max-width: var(--max-width);
  margin: 0 auto;
  ${props =>
    props.scrollDirection === 'up' &&
    !props.scrolledToTop &&
    css`
      height: 70px;
    `};
  ${props =>
    props.scrollDirection === 'down' &&
    !props.scrolledToTop &&
    css`
      height: 70px;
    `};
  @media screen and (max-width: 800px) {
    width: calc(100% - 50px);
    margin: 0 25px;
  }
`
const HeaderLogo = styled.div`
  &.logo--small a {
    width: 42px;
    height: 42px;
  }
  &.logo--large a {
    width: 58px;
    height: 58px;
  }
  a {
    display: block;
    border-radius: 50%;
    overflow: hidden;
    &:hover,
    &:focus {
      svg {
        #bg {
          fill: var(--grey-hover);
          transition: var(--transition);
        }
      }
    }
    svg {
      #bg {
        fill: var(--white);
        user-select: none;
        transition: var(--transition);
      }
    }
  }
`
const HeaderNavigation = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 58px;
  a.nav-link {
    display: inline-block;
    padding: 10px 20px 13px;
    margin: 0 2px;
    color: var(--white);
    font-family: var(--font-condensed);
    font-size: var(--fz-lg);
    font-weight: 500;
    line-height: 1;
    border-radius: 4px;
    border: 2px solid transparent;
    background-color: transparent;
    transition: var(--transition);
    &:hover {
      color: var(--white);
      border: 2px solid var(--grey-hover);
      background-color: var(--grey-hover);
    }
    &.quote-btn {
      margin: 0 0 0 20px;
      border: 2px solid var(--secondary);
      background-color: var(--secondary);
      &:hover {
        border: 2px solid var(--grey-hover);
        background-color: var(--grey-hover);
      }
    }
  }
  a.nav-link.active {
    color: var(--white);
    border: 2px solid var(--grey-hover);
    background-color: transparent;
    transition: none;
    &:hover {
      background-color: var(--grey-hover);
    }
    &.quote-btn {
      border: 2px solid var(--secondary);
      background-color: var(--secondary);
      &:hover {
        border: 2px solid var(--secondary);
        background-color: var(--secondary);
      }
    }
  }

  @media screen and (max-width: 1023px) {
    display: none;
  }
`
const HeaderMenu = styled.div`
  display: none;
  @media screen and (max-width: 1023px) {
    display: block;
  }
`

const Header = () => {
  const [scrolledToTop, setScrolledToTop] = useState(true)
  const { preloaderExecuted, navigationExecuted, appNavigationExecuted } = useShop()
  const scrollDirection = useScrollDirection('down')

  const handleScroll = () => {
    setScrolledToTop(window.pageYOffset < 50)
  }

  useEffect(() => {
    let timer = setTimeout(function () {
      appNavigationExecuted(true)
    }, 1000)
    window.addEventListener('scroll', handleScroll)
    // delete
    return () => {
      clearTimeout(timer)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <HeaderWrapper scrollDirection={scrollDirection} scrolledToTop={scrolledToTop}>
      <HeaderContainer scrollDirection={scrollDirection} scrolledToTop={scrolledToTop}>
        <HeaderLogo className="logo--large" tabIndex="-1">
          <Link to="/" aria-label="Home">
            {preloaderExecuted && navigationExecuted ? (
              <IconLogo />
            ) : (
              <motion.div animate={{ opacity: [0, 1] }} transition={{ delay: 0.1, duration: 0.3 }} className="start-opacity">
                <IconLogo />
              </motion.div>
            )}
          </Link>
        </HeaderLogo>
        <HeaderNavigation>
          {preloaderExecuted && navigationExecuted
            ? headerLinks.map(({ url, name }, i) => (
                <NavLink to={url} key={i} className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
                  {name}
                </NavLink>
              ))
            : headerLinks.map(({ url, name }, i) => (
                <motion.div key={i} custom={i} animate={{ y: [-30, 0], opacity: [0, 1] }} transition={{ delay: i * 0.1 + 0.1, duration: 0.3 }} className="start-opacity">
                  <NavLink to={url} className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
                    {name}
                  </NavLink>
                </motion.div>
              ))}
          {preloaderExecuted && navigationExecuted ? (
            <NavLink to="/get-a-free-quote" className={({ isActive }) => (isActive ? 'nav-link quote-btn active' : 'nav-link quote-btn')}>
              Get a free quote
            </NavLink>
          ) : (
            <motion.div animate={{ y: [-30, 0], opacity: [0, 1] }} transition={{ delay: 0.6, duration: 0.3 }} className="start-opacity">
              <NavLink to="/get-a-free-quote" className={({ isActive }) => (isActive ? 'nav-link quote-btn active' : 'nav-link quote-btn')}>
                Get a free quote
              </NavLink>
            </motion.div>
          )}
        </HeaderNavigation>
        <HeaderMenu>
          {preloaderExecuted && navigationExecuted ? (
            <Menu />
          ) : (
            <motion.div animate={{ opacity: [0, 1] }} transition={{ delay: 0.3, duration: 0.2 }} className="start-opacity">
              <Menu />
            </motion.div>
          )}
        </HeaderMenu>
      </HeaderContainer>
    </HeaderWrapper>
  )
}

export default Header
